import { Link } from 'react-router-dom'
import { Instagram } from 'lucide-react'

const GIGAFISH_URL = 'https://gigafish.co'

function LinkedinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

const SOCIAL_LINKS = [
  { href: 'https://www.linkedin.com/company/lynas-rare-earths-ltd', icon: LinkedinIcon, label: 'LinkedIn' },
  { href: 'https://x.com/LynasRareEarths', icon: XIcon, label: 'X' },
  { href: 'https://www.instagram.com/wearelynasmalaysia', icon: Instagram, label: 'Instagram' },
]

export default function Footer() {
  return (
    <footer className="relative bg-accent overflow-hidden">
      <div className="container relative z-10 py-12 md:py-16">
        <div className="flex flex-col items-center text-center md:flex-row md:items-start md:justify-between md:text-left gap-10">
          <div>
            <Link to="/" className="inline-block">
              <img
                src="/assets/lynas-logo-reverse.png"
                alt="Lynas Rare Earths"
                className="h-10 md:h-12 object-contain mx-auto md:mx-0 object-left"
              />
            </Link>
            <p className="mt-4 text-sm text-white/90 max-w-sm mx-auto md:mx-0">
              The world&apos;s only significant producer of separated rare earth materials outside China.
            </p>
            <div className="mt-4 flex items-center justify-center md:justify-start gap-3">
              {SOCIAL_LINKS.map(({ href, icon: Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors"
                  aria-label={label}
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-8 md:gap-12">
            <div className="text-center md:text-left">
              <h4 className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-4">Corporate</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-sm text-white/90 hover:text-white transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/investors" className="text-sm text-white/90 hover:text-white transition-colors">
                    Investor Relations
                  </Link>
                </li>
              </ul>
            </div>
            <div className="text-center md:text-left">
              <h4 className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-4">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/privacy" className="text-sm text-white/90 hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/disclaimer" className="text-sm text-white/90 hover:text-white transition-colors">
                    Disclaimer
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-white/20 flex flex-col items-center text-center sm:flex-row sm:items-stretch sm:justify-between sm:text-left gap-6">
          <div>
            <p className="text-sm text-white/70">
              © {new Date().getFullYear()} All rights reserved.
            </p>
            <p className="text-xs text-white/50 mt-3 leading-relaxed">
            Lynas Rare Earths Limited<br />
              ACN 009 066 648<br />
              Lynas Malaysia Sdn. Bhd<br />
              Registration Number: 200601032530 (752289-D)
            </p>
          </div>
          <div className="flex flex-col items-center sm:items-end justify-between min-h-0">
            <img
              src="/assets/lynas-slogan.png"
              alt="Advancing Hand In Hand With You"
              className="h-6 md:h-7 object-contain object-right mix-blend-lighten opacity-95"
            />
            <a
              href={GIGAFISH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-white/70 hover:text-white transition-colors self-center sm:self-end mt-4 sm:mt-0"
            >
              <span className="text-xs font-medium">Digital experience by</span>
              <img src="/assets/gigafish-wordmark.svg" alt="GIGAFISH" className="h-2.5 brightness-0 invert opacity-90" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
